/**
 * Practido Scratch 3.0 AST Expression & Nested Logic Evaluator
 * Recursively parses and transpiles nested Scratch block expressions across all categories:
 * Operators, Variables/Lists, Sensing, Motion, Looks, Sound, and Procedures.
 */

export const getFieldName = (block, fieldName = "VARIABLE") => {
  if (!block || !block.fields) return "my_variable";
  const fieldVal = block.fields[fieldName] || Object.values(block.fields)[0];
  if (!fieldVal) return "my_variable";
  if (Array.isArray(fieldVal)) {
    return String(fieldVal[0] || "my_variable");
  }
  if (typeof fieldVal === "object" && fieldVal.value !== undefined) {
    return String(fieldVal.value);
  }
  if (typeof fieldVal === "string") {
    return fieldVal;
  }
  return "my_variable";
};

/**
 * Normalizes user-entered Python/JS menu values into Scratch VM internal storage format.
 * Covers motion, sensing, control, looks, events, and dropdowns.
 */
export const toScratchMenuValue = (menuOpcode, value) => {
  if (value === undefined || value === null) return value;
  const str = String(value).trim();
  const clean = str.replace(/['"]/g, "").trim();
  const lower = clean.toLowerCase();

  switch (menuOpcode) {
    case "motion_goto_menu":
    case "motion_glideto_menu": {
      if (lower === "random" || lower === "random position" || lower === "random_position" || lower === "_random_") {
        return "_random_";
      }
      if (lower === "mouse" || lower === "mouse-pointer" || lower === "mouse_pointer" || lower === "_mouse_") {
        return "_mouse_";
      }
      return clean;
    }

    case "motion_pointtowards_menu":
    case "sensing_distancetomenu": {
      if (lower === "mouse" || lower === "mouse-pointer" || lower === "mouse_pointer" || lower === "_mouse_") {
        return "_mouse_";
      }
      if (lower === "random" || lower === "random direction" || lower === "_random_") {
        return "_random_";
      }
      return clean;
    }

    case "sensing_touchingobjectmenu": {
      if (lower === "mouse" || lower === "mouse-pointer" || lower === "mouse_pointer" || lower === "_mouse_") {
        return "_mouse_";
      }
      if (lower === "edge" || lower === "_edge_") {
        return "_edge_";
      }
      return clean;
    }

    case "control_create_clone_of_menu": {
      if (!lower || lower === "myself" || lower === "_myself_" || lower === "self") {
        return "_myself_";
      }
      return clean;
    }

    case "sensing_of_object_menu": {
      if (lower === "stage" || lower === "_stage_") {
        return "_stage_";
      }
      return clean;
    }

    case "sensing_keyoptions":
    case "event_whenkeypressed": {
      if (lower === "up_arrow" || lower === "up" || lower === "up arrow") return "up arrow";
      if (lower === "down_arrow" || lower === "down" || lower === "down arrow") return "down arrow";
      if (lower === "right_arrow" || lower === "right" || lower === "right arrow") return "right arrow";
      if (lower === "left_arrow" || lower === "left" || lower === "left arrow") return "left arrow";
      return lower;
    }

    default:
      return clean;
  }
};

/**
 * Normalizes internal Scratch VM dropdown keys into clean, human-readable code representation.
 */
export const toCanonicalCodeMenuValue = (menuOpcode, value) => {
  if (value === undefined || value === null) return value;
  const str = String(value).trim();
  const clean = str.replace(/['"]/g, "").trim();

  switch (menuOpcode) {
    case "motion_goto_menu":
    case "motion_glideto_menu": {
      if (clean === "_random_" || clean.toLowerCase() === "random") return "random position";
      if (clean === "_mouse_" || clean.toLowerCase() === "mouse") return "mouse-pointer";
      return clean;
    }

    case "motion_pointtowards_menu":
    case "sensing_distancetomenu": {
      if (clean === "_mouse_" || clean.toLowerCase() === "mouse") return "mouse-pointer";
      if (clean === "_random_") return "random position";
      return clean;
    }

    case "sensing_touchingobjectmenu": {
      if (clean === "_mouse_" || clean.toLowerCase() === "mouse") return "mouse-pointer";
      if (clean === "_edge_") return "edge";
      return clean;
    }

    case "control_create_clone_of_menu": {
      if (!clean || clean === "_myself_") return "myself";
      return clean;
    }

    case "sensing_of_object_menu": {
      if (clean === "_stage_" || clean.toLowerCase() === "stage") return "Stage";
      return clean;
    }

    default:
      return clean;
  }
};

const isCodeExpression = (s) => {
  if (typeof s !== "string") return false;
  const trimmed = s.trim();
  if (trimmed === "True" || trimmed === "False" || trimmed === "true" || trimmed === "false") return true;
  if (/^(?:random\.randint|randint|round|abs|math\.\w+|len|input)\s*\(.*\)$/.test(trimmed)) return true;
  if (/^[a-zA-Z0-9_.]+\s*(?:==|!=|<=|>=|<|>|\+|-|\*|\/|%)\s*[a-zA-Z0-9_.]+/.test(trimmed)) return true;
  if (/^[a-zA-Z0-9_]+\.[a-zA-Z0-9_]+(?:\(.*\))?$/.test(trimmed)) return true;
  return false;
};

export const evaluateScratchInput = (block, allBlocks, inputName, targetLang = "python", varName = "sprite1") => {
  if (!block || !block.inputs || !block.inputs[inputName]) return null;
  const inputData = block.inputs[inputName];

  if (Array.isArray(inputData) && inputData.length >= 2) {
    const shadow = inputData[1];
    if (Array.isArray(shadow) && shadow.length >= 2) {
      const val = shadow[1];
      if (typeof val === "number") return String(val);
      if (typeof val === "string") {
        if (allBlocks && allBlocks[val]) {
          return evaluateBlockExpression(allBlocks[val], allBlocks, targetLang, varName);
        }
        return (isCodeExpression(val) || !isNaN(val)) ? String(val) : `"${val}"`;
      }
      return String(val);
    } else if (typeof shadow === "string" && allBlocks && allBlocks[shadow]) {
      return evaluateBlockExpression(allBlocks[shadow], allBlocks, targetLang, varName);
    } else if (typeof shadow === "number" || typeof shadow === "string") {
      return (isCodeExpression(shadow) || !isNaN(shadow)) ? String(shadow) : `"${shadow}"`;
    }
  } else if (inputData && typeof inputData === "object" && !Array.isArray(inputData)) {
    const shadow = inputData.block || inputData.shadow;
    if (typeof shadow === "string" && allBlocks && allBlocks[shadow]) {
      return evaluateBlockExpression(allBlocks[shadow], allBlocks, targetLang, varName);
    }
  }
  return null;
};

export const evaluateBlockExpression = (block, allBlocks, targetLang = "python", varName = "sprite1") => {
  if (!block) return "0";
  const opcode = block.opcode;

  switch (opcode) {
    // -----------------------------------------------------
    // 1. Math Operators
    // -----------------------------------------------------
    case "operator_add": {
      const a = evaluateScratchInput(block, allBlocks, "NUM1", targetLang) || "0";
      const b = evaluateScratchInput(block, allBlocks, "NUM2", targetLang) || "0";
      return `(${a} + ${b})`;
    }

    case "operator_subtract": {
      const a = evaluateScratchInput(block, allBlocks, "NUM1", targetLang) || "0";
      const b = evaluateScratchInput(block, allBlocks, "NUM2", targetLang) || "0";
      return `(${a} - ${b})`;
    }

    case "operator_multiply": {
      const a = evaluateScratchInput(block, allBlocks, "NUM1", targetLang) || "0";
      const b = evaluateScratchInput(block, allBlocks, "NUM2", targetLang) || "0";
      return `(${a} * ${b})`;
    }

    case "operator_divide": {
      const a = evaluateScratchInput(block, allBlocks, "NUM1", targetLang) || "0";
      const b = evaluateScratchInput(block, allBlocks, "NUM2", targetLang) || "0";
      return `(${a} / ${b})`;
    }

    case "operator_random": {
      const min = evaluateScratchInput(block, allBlocks, "FROM", targetLang) || "1";
      const max = evaluateScratchInput(block, allBlocks, "TO", targetLang) || "10";
      if (targetLang === "javascript") {
        return `Math.floor(Math.random() * (${max} - ${min} + 1)) + ${min}`;
      }
      return `random.randint(${min}, ${max})`;
    }

    // -----------------------------------------------------
    // 2. Logic & Boolean Comparisons
    // -----------------------------------------------------
    case "operator_gt": {
      const a = evaluateScratchInput(block, allBlocks, "OPERAND1", targetLang) || "0";
      const b = evaluateScratchInput(block, allBlocks, "OPERAND2", targetLang) || "0";
      return `(${a} > ${b})`;
    }

    case "operator_lt": {
      const a = evaluateScratchInput(block, allBlocks, "OPERAND1", targetLang) || "0";
      const b = evaluateScratchInput(block, allBlocks, "OPERAND2", targetLang) || "0";
      return `(${a} < ${b})`;
    }

    case "operator_equals": {
      const a = evaluateScratchInput(block, allBlocks, "OPERAND1", targetLang) || "0";
      const b = evaluateScratchInput(block, allBlocks, "OPERAND2", targetLang) || "0";
      return `(${a} == ${b})`;
    }

    case "operator_and": {
      const a = evaluateScratchInput(block, allBlocks, "OPERAND1", targetLang) || "True";
      const b = evaluateScratchInput(block, allBlocks, "OPERAND2", targetLang) || "True";
      return targetLang === "javascript" ? `(${a} && ${b})` : `(${a} and ${b})`;
    }

    case "operator_or": {
      const a = evaluateScratchInput(block, allBlocks, "OPERAND1", targetLang) || "False";
      const b = evaluateScratchInput(block, allBlocks, "OPERAND2", targetLang) || "False";
      return targetLang === "javascript" ? `(${a} || ${b})` : `(${a} or ${b})`;
    }

    case "operator_not": {
      const a = evaluateScratchInput(block, allBlocks, "OPERAND", targetLang) || "False";
      return targetLang === "javascript" ? `!(${a})` : `not (${a})`;
    }

    // -----------------------------------------------------
    // 3. String Operators
    // -----------------------------------------------------
    case "operator_join": {
      const s1 = evaluateScratchInput(block, allBlocks, "STRING1", targetLang) || '""';
      const s2 = evaluateScratchInput(block, allBlocks, "STRING2", targetLang) || '""';
      return targetLang === "javascript" ? `(${s1} + ${s2})` : `f"{${s1}}{${s2}}"`;
    }

    case "operator_length": {
      const str = evaluateScratchInput(block, allBlocks, "STRING", targetLang) || '""';
      return targetLang === "javascript" ? `${str}.length` : `len(${str})`;
    }
    
    case "operator_letter_of": {
      const letterIdx = evaluateScratchInput(block, allBlocks, "LETTER", targetLang) || "1";
      const str = evaluateScratchInput(block, allBlocks, "STRING", targetLang) || '""';
      return targetLang === "javascript" ? `${str}[${letterIdx} - 1]` : `${str}[int(${letterIdx}) - 1]`;
    }
    
    case "operator_contains": {
      const s1 = evaluateScratchInput(block, allBlocks, "STRING1", targetLang) || '""';
      const s2 = evaluateScratchInput(block, allBlocks, "STRING2", targetLang) || '""';
      return targetLang === "javascript" ? `${s1}.includes(${s2})` : `(${s2} in ${s1})`;
    }
    
    case "operator_mod": {
      const num1 = evaluateScratchInput(block, allBlocks, "NUM1", targetLang) || "0";
      const num2 = evaluateScratchInput(block, allBlocks, "NUM2", targetLang) || "1";
      return `(${num1} % ${num2})`;
    }
    
    case "operator_round": {
      const num = evaluateScratchInput(block, allBlocks, "NUM", targetLang) || "0";
      return targetLang === "javascript" ? `Math.round(${num})` : `round(${num})`;
    }
    
    case "operator_mathop": {
      const num = evaluateScratchInput(block, allBlocks, "NUM", targetLang) || "0";
      const operator = getFieldName(block, "OPERATOR");
      
      switch (operator) {
        case "abs": return targetLang === "javascript" ? `Math.abs(${num})` : `abs(${num})`;
        case "floor": return targetLang === "javascript" ? `Math.floor(${num})` : `math.floor(${num})`;
        case "ceiling": return targetLang === "javascript" ? `Math.ceil(${num})` : `math.ceil(${num})`;
        case "sqrt": return targetLang === "javascript" ? `Math.sqrt(${num})` : `math.sqrt(${num})`;
        case "sin": return targetLang === "javascript" ? `Math.sin(${num})` : `math.sin(${num})`;
        case "cos": return targetLang === "javascript" ? `Math.cos(${num})` : `math.cos(${num})`;
        case "tan": return targetLang === "javascript" ? `Math.tan(${num})` : `math.tan(${num})`;
        case "asin": return targetLang === "javascript" ? `Math.asin(${num})` : `math.asin(${num})`;
        case "acos": return targetLang === "javascript" ? `Math.acos(${num})` : `math.acos(${num})`;
        case "atan": return targetLang === "javascript" ? `Math.atan(${num})` : `math.atan(${num})`;
        case "ln": return targetLang === "javascript" ? `Math.log(${num})` : `math.log(${num})`;
        case "log": return targetLang === "javascript" ? `Math.log10(${num})` : `math.log10(${num})`;
        case "e ^": return targetLang === "javascript" ? `Math.exp(${num})` : `math.exp(${num})`;
        case "10 ^": return targetLang === "javascript" ? `Math.pow(10, ${num})` : `(10 ** ${num})`;
        default: return num;
      }
    }

    // -----------------------------------------------------
    // 4. Variables & Lists
    // -----------------------------------------------------
    case "data_variable": {
      const rawVar = getFieldName(block, "VARIABLE");
      return rawVar.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
    }

    case "data_itemoflist": {
      const idx = evaluateScratchInput(block, allBlocks, "INDEX", targetLang) || "1";
      const listName = getFieldName(block, "LIST").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
      return targetLang === "javascript" ? `${listName}[${idx} - 1]` : `${listName}[int(${idx}) - 1]`;
    }

    case "data_lengthoflist": {
      const listName = getFieldName(block, "LIST").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
      return targetLang === "javascript" ? `${listName}.length` : `len(${listName})`;
    }
    
    case "data_listcontainsitem": {
      const listName = getFieldName(block, "LIST").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
      const item = evaluateScratchInput(block, allBlocks, "ITEM", targetLang) || '""';
      return targetLang === "javascript" ? `${listName}.includes(${item})` : `(${item} in ${listName})`;
    }

    case "data_itemnumoflist": {
      const listName = getFieldName(block, "LIST").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
      const item = evaluateScratchInput(block, allBlocks, "ITEM", targetLang) || '""';
      return targetLang === "javascript"
        ? `(${listName}.indexOf(${item}) + 1)`
        : `(${listName}.index(${item}) + 1 if ${item} in ${listName} else 0)`;
    }

    // -----------------------------------------------------
    // 5. Sensing & Inputs
    // -----------------------------------------------------
    case "sensing_answer":
      return "user_answer";

    case "sensing_mousex":
      return targetLang === "javascript" ? "mouse.x" : "mouse_x";

    case "sensing_mousey":
      return targetLang === "javascript" ? "mouse.y" : "mouse_y";
      
    case "sensing_mousedown":
      return targetLang === "javascript" ? "mouse.isDown" : "mouse_down";

    case "sensing_timer":
      return targetLang === "javascript" ? "(Date.now() - timerStart) / 1000" : "(time.time() - timer_start)";
      
    case "sensing_loudness":
      return targetLang === "javascript" ? "microphone.loudness" : "microphone.loudness";
      
    case "sensing_keypressed": {
      let keyOpt = evaluateScratchInput(block, allBlocks, "KEY_OPTION", targetLang);
      if (!keyOpt || keyOpt === '""' || keyOpt === "0") {
        keyOpt = getFieldName(block, "KEY_OPTION") || "space";
      }
      keyOpt = String(keyOpt).replace(/["']/g, "");
      return targetLang === "javascript" ? `keyboard.isPressed("${keyOpt}")` : `keyboard.is_pressed("${keyOpt}")`;
    }

    case "sensing_touchingobject": {
      let obj = evaluateScratchInput(block, allBlocks, "TOUCHINGOBJECTMENU", targetLang, varName);
      if (!obj || obj === '""' || obj === "0") {
        obj = getFieldName(block, "TOUCHINGOBJECTMENU") || "mouse-pointer";
      }
      obj = String(obj).replace(/["']/g, "");
      obj = toCanonicalCodeMenuValue("sensing_touchingobjectmenu", obj);
      return targetLang === "javascript" ? `${varName}.isTouching("${obj}")` : `${varName}.is_touching("${obj}")`;
    }

    case "sensing_touchingcolor": {
      const color = evaluateScratchInput(block, allBlocks, "COLOR", targetLang, varName) || '"#ff0000"';
      return targetLang === "javascript" ? `${varName}.isTouchingColor(${color})` : `${varName}.is_touching_color(${color})`;
    }

    case "sensing_coloristouchingcolor": {
      const col1 = evaluateScratchInput(block, allBlocks, "COLOR", targetLang, varName) || '"#00ff00"';
      const col2 = evaluateScratchInput(block, allBlocks, "COLOR2", targetLang, varName) || '"#ff0000"';
      return targetLang === "javascript"
        ? `${varName}.colorIsTouchingColor(${col1}, ${col2})`
        : `${varName}.color_is_touching_color(${col1}, ${col2})`;
    }
    
    case "sensing_distanceto": {
      let obj = evaluateScratchInput(block, allBlocks, "DISTANCETOMENU", targetLang, varName);
      if (!obj || obj === '""' || obj === "0") {
        obj = getFieldName(block, "DISTANCETOMENU") || "mouse-pointer";
      }
      obj = String(obj).replace(/["']/g, "");
      obj = toCanonicalCodeMenuValue("sensing_distancetomenu", obj);
      return targetLang === "javascript" ? `${varName}.distanceTo("${obj}")` : `${varName}.distance_to("${obj}")`;
    }

    case "sensing_of": {
      const prop = getFieldName(block, "PROPERTY");
      let obj = evaluateScratchInput(block, allBlocks, "OBJECT", targetLang, varName) || '"_stage_"';
      const cleanProp = prop.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
      obj = String(obj).replace(/["'_]/g, "").toLowerCase();
      const targetObj = obj === "stage" ? "stage" : obj || varName;
      return `${targetObj}.${cleanProp}`;
    }

    case "sensing_dayssince2000":
      return targetLang === "javascript"
        ? "((Date.now() - new Date(2000, 0, 1).getTime()) / 86400000)"
        : "((time.time() - 946684800) / 86400)";
    
    case "sensing_current": {
      const currentOpt = getFieldName(block, "CURRENTMENU");
      if (targetLang === "javascript") {
        switch(currentOpt) {
          case 'YEAR': return 'new Date().getFullYear()';
          case 'MONTH': return '(new Date().getMonth() + 1)';
          case 'DATE': return 'new Date().getDate()';
          case 'DAYOFWEEK': return '(new Date().getDay() + 1)';
          case 'HOUR': return 'new Date().getHours()';
          case 'MINUTE': return 'new Date().getMinutes()';
          case 'SECOND': return 'new Date().getSeconds()';
          default: return '0';
        }
      } else {
        return `get_current_time("${currentOpt}")`;
      }
    }
    
    case "sensing_username":
      return '"practido_user"';
      
    // -----------------------------------------------------
    // 6. Motion Reporters
    // -----------------------------------------------------
    case "motion_xposition":
      return targetLang === "javascript" ? `${varName}.x` : `${varName}.x`;
      
    case "motion_yposition":
      return targetLang === "javascript" ? `${varName}.y` : `${varName}.y`;
      
    case "motion_direction":
      return targetLang === "javascript" ? `${varName}.direction` : `${varName}.direction`;
      
    // -----------------------------------------------------
    // 7. Looks Reporters
    // -----------------------------------------------------
    case "looks_size":
      return targetLang === "javascript" ? `${varName}.size` : `${varName}.size`;
      
    case "looks_costumenumbername": {
      const returnType = getFieldName(block, "NUMBER_NAME") === "number" ? "index" : "name";
      return targetLang === "javascript" ? `${varName}.costume.${returnType}` : `${varName}.costume_${returnType}`;
    }
      
    case "looks_backdropnumbername": {
      const bdReturnType = getFieldName(block, "NUMBER_NAME") === "number" ? "index" : "name";
      return targetLang === "javascript" ? `stage.backdrop.${bdReturnType}` : `stage.backdrop_${bdReturnType}`;
    }

    // -----------------------------------------------------
    // 8. Sound Reporters
    // -----------------------------------------------------
    case "sound_volume":
      return targetLang === "javascript" ? `${varName}.volume` : `${varName}.volume`;

    // -----------------------------------------------------
    // 9. Custom Blocks (Procedure Argument Reporters)
    // -----------------------------------------------------
    case "argument_reporter_string_number":
    case "argument_reporter_boolean": {
      const argName = getFieldName(block, "VALUE");
      return argName.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
    }

    // -----------------------------------------------------
    // 10. Shadow Dropdown Menus
    // -----------------------------------------------------
    case "sound_sounds_menu":
      return `"${getFieldName(block, "SOUND_MENU")}"`;

    case "looks_costume":
      return `"${getFieldName(block, "COSTUME")}"`;

    case "looks_backdrops":
      return `"${getFieldName(block, "BACKDROP")}"`;

    case "event_broadcast_menu":
      return `"${getFieldName(block, "BROADCAST_OPTION")}"`;

    case "sensing_keyoptions":
      return `"${getFieldName(block, "KEY_OPTION")}"`;

    case "sensing_touchingobjectmenu": {
      const m = getFieldName(block, "TOUCHINGOBJECTMENU");
      return `"${toCanonicalCodeMenuValue("sensing_touchingobjectmenu", m)}"`;
    }

    case "sensing_distancetomenu": {
      const m = getFieldName(block, "DISTANCETOMENU");
      return `"${toCanonicalCodeMenuValue("sensing_distancetomenu", m)}"`;
    }

    case "motion_goto_menu": {
      const m = getFieldName(block, "TO");
      return `"${toCanonicalCodeMenuValue("motion_goto_menu", m)}"`;
    }

    case "motion_glideto_menu": {
      const m = getFieldName(block, "TO");
      return `"${toCanonicalCodeMenuValue("motion_glideto_menu", m)}"`;
    }

    case "motion_pointtowards_menu": {
      const m = getFieldName(block, "TOWARDS");
      return `"${toCanonicalCodeMenuValue("motion_pointtowards_menu", m)}"`;
    }

    case "control_create_clone_of_menu": {
      const m = getFieldName(block, "CLONE_OPTION");
      return `"${toCanonicalCodeMenuValue("control_create_clone_of_menu", m)}"`;
    }

    case "sensing_of_object_menu": {
      const m = getFieldName(block, "OBJECT");
      return `"${toCanonicalCodeMenuValue("sensing_of_object_menu", m)}"`;
    }

    case "teachableMachine_menu_classesMenu": {
      const m = getFieldName(block, "classesMenu") || getFieldName(block, "CLASS") || "Thumbs Up";
      return `"${m}"`;
    }

    case "teachableMachine_menu_modelTypeMenu": {
      const m = getFieldName(block, "modelTypeMenu") || getFieldName(block, "TYPE") || "image";
      return `"${m}"`;
    }

    case "teachableMachine_menu_videoStateMenu": {
      const m = getFieldName(block, "videoStateMenu") || getFieldName(block, "VIDEO_STATE") || "on";
      return `"${m}"`;
    }

    case "teachableMachine_isClassDetected": {
      let cls = evaluateScratchInput(block, allBlocks, "CLASS", targetLang, varName);
      if (!cls || cls === '""' || cls === "0") {
        cls = `"${getFieldName(block, "CLASS") || "Thumbs Up"}"`;
      }
      return targetLang === "javascript" ? `tm.isClassDetected(${cls})` : `tm.is_class_detected(${cls})`;
    }

    case "teachableMachine_getDetectedClass": {
      return targetLang === "javascript" ? `tm.getDetectedClass()` : `tm.get_detected_class()`;
    }

    case "teachableMachine_getClassConfidence": {
      let cls = evaluateScratchInput(block, allBlocks, "CLASS", targetLang, varName);
      if (!cls || cls === '""' || cls === "0") {
        cls = `"${getFieldName(block, "CLASS") || "Thumbs Up"}"`;
      }
      return targetLang === "javascript" ? `tm.getConfidence(${cls})` : `tm.get_confidence(${cls})`;
    }

    // -----------------------------------------------------
    // 11. Primitive Shadows (Numbers, Strings, Colours)
    // -----------------------------------------------------
    case "math_number":
    case "math_positive_number":
    case "math_whole_number":
    case "math_integer":
    case "math_angle": {
      const val = getFieldName(block, "NUM");
      return isNaN(Number(val)) ? "0" : String(val);
    }

    case "text": {
      const val = getFieldName(block, "TEXT");
      return `"${val}"`;
    }

    case "colour_picker": {
      const val = getFieldName(block, "COLOUR");
      return `"${val}"`;
    }

    default:
      if (block.fields) {
        return `"${getFieldName(block)}"`;
      }
      return "0";
  }
};

/**
 * Sanitizes variable and list names into valid Python/JavaScript identifiers.
 */
export const cleanIdentifier = (name) => {
  let clean = (name || "").replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
  if (/^[0-9]/.test(clean)) clean = "_" + clean;
  return clean || "item";
};

/**
 * Extracts top-level declarations for variables and lists in a Scratch target.
 * Ensures generated Python and JavaScript have valid declarations before use.
 */
export const getDeclarations = (target, stageTarget, lang = "python") => {
  const decls = [];
  const declaredLists = new Set();
  const declaredVars = new Set();

  const listBlockOpcodeSet = new Set([
    "data_addtolist", "data_deleteoflist", "data_deletealloflist",
    "data_insertatlist", "data_replaceitemoflist", "data_itemoflist",
    "data_itemnumoflist", "data_lengthoflist", "data_listcontainsitem",
    "data_showlist", "data_hidelist"
  ]);

  const varBlockOpcodeSet = new Set([
    "data_setvariableto", "data_changevariableby", "data_variable",
    "data_showvariable", "data_hidevariable"
  ]);

  const referencedLists = new Set();
  const referencedVars = new Set();

  if (target && target.blocks) {
    Object.values(target.blocks).forEach((b) => {
      if (b && typeof b === "object") {
        if (listBlockOpcodeSet.has(b.opcode)) {
          const lName = (b.fields && b.fields.LIST && b.fields.LIST[0]) || "";
          if (lName) referencedLists.add(lName);
        }
        if (varBlockOpcodeSet.has(b.opcode)) {
          const vName = (b.fields && b.fields.VARIABLE && b.fields.VARIABLE[0]) || "";
          if (vName) referencedVars.add(vName);
        }
      }
    });
  }

  // 1. Process Lists
  const allListEntries = [];
  if (target && target.lists) {
    Object.values(target.lists).forEach((l) => {
      if (Array.isArray(l)) allListEntries.push(l);
    });
  }
  // Include global lists from stageTarget when transpiling a sprite
  if (stageTarget && stageTarget.lists && !target?.isStage) {
    Object.values(stageTarget.lists).forEach((l) => {
      if (Array.isArray(l)) allListEntries.push(l);
    });
  }

  allListEntries.forEach(([rawName, items]) => {
    const clean = cleanIdentifier(rawName);
    if (!declaredLists.has(clean)) {
      const isTargetOwner = target && target.lists && Object.values(target.lists).some(l => l[0] === rawName);
      const isReferenced = referencedLists.has(rawName);

      // If target is Stage: ONLY declare lists if Stage blocks actually reference them
      if (target?.isStage) {
        if (!isReferenced) return;
      }

      declaredLists.add(clean);
      const initialItems = Array.isArray(items) && items.length > 0
        ? `[${items.map(it => JSON.stringify(it)).join(", ")}]`
        : "[]";
      if (lang === "python") {
        decls.push(`${clean} = ${initialItems}`);
      } else if (lang === "javascript") {
        decls.push(`let ${clean} = ${initialItems};`);
      }
    }
  });

  referencedLists.forEach((rawName) => {
    const clean = cleanIdentifier(rawName);
    if (!declaredLists.has(clean)) {
      declaredLists.add(clean);
      if (lang === "python") {
        decls.push(`${clean} = []`);
      } else if (lang === "javascript") {
        decls.push(`let ${clean} = [];`);
      }
    }
  });

  // 2. Process Variables
  const allVarEntries = [];
  if (target && target.variables) {
    Object.values(target.variables).forEach((v) => {
      if (Array.isArray(v)) allVarEntries.push(v);
    });
  }
  // Include global variables from stageTarget when transpiling a sprite
  if (stageTarget && stageTarget.variables && !target?.isStage) {
    Object.values(stageTarget.variables).forEach((v) => {
      if (Array.isArray(v)) allVarEntries.push(v);
    });
  }

  allVarEntries.forEach(([rawName, val]) => {
    const clean = cleanIdentifier(rawName);
    if (rawName.toLowerCase() === "my variable" && !referencedVars.has(rawName)) {
      return;
    }
    if (!declaredVars.has(clean)) {
      const isTargetOwner = target && target.variables && Object.values(target.variables).some(v => v[0] === rawName);
      const isReferenced = referencedVars.has(rawName);

      // If target is Stage: ONLY declare variables if Stage blocks actually reference them
      if (target?.isStage) {
        if (!isReferenced) return;
      }

      declaredVars.add(clean);

      // Numeric variables must always initialize to 0 in declarations (runtime execution residue like 6 or 10 should never leak into code)
      let formattedVal = 0;
      if (typeof val === "string" && isNaN(Number(val)) && val.trim() !== "") {
        formattedVal = JSON.stringify(val);
      } else {
        formattedVal = 0;
      }

      if (lang === "python") {
        decls.push(`${clean} = ${formattedVal}`);
      } else if (lang === "javascript") {
        decls.push(`let ${clean} = ${formattedVal};`);
      }
    }
  });

  referencedVars.forEach((rawName) => {
    const clean = cleanIdentifier(rawName);
    if (rawName.toLowerCase() === "my variable" && !referencedVars.has(rawName)) return;
    if (!declaredVars.has(clean)) {
      declaredVars.add(clean);
      if (lang === "python") {
        decls.push(`${clean} = 0`);
      } else if (lang === "javascript") {
        decls.push(`let ${clean} = 0;`);
      }
    }
  });

  return decls;
};

/**
 * Deterministically sorts top-level Scratch block IDs from top-to-bottom (y) and left-to-right (x).
 * Guarantees consistent ordering across Scratch-to-Python, Scratch-to-JS, and Python/JS-to-Scratch sync.
 */
export const sortTopLevelBlockKeys = (blocks) => {
  if (!blocks || typeof blocks !== "object") return [];
  return Object.keys(blocks)
    .filter((k) => blocks[k] && typeof blocks[k] === "object" && blocks[k].topLevel)
    .sort((a, b) => {
      const blockA = blocks[a];
      const blockB = blocks[b];
      const yA = Number(blockA.y) || 0;
      const yB = Number(blockB.y) || 0;
      if (Math.abs(yA - yB) > 15) {
        return yA - yB;
      }
      const xA = Number(blockA.x) || 0;
      const xB = Number(blockB.x) || 0;
      return xA - xB;
    });
};


